import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layout } from '../components/layout';
import {
  Target,
  Eye,
  Heart,
  MapPin,
  CheckCircle2,
  Building2,
  Calendar,
  Recycle,
  Map,
  LucideIcon,
} from 'lucide-react';
import { OptimizedImage } from '../components/ui/OptimizedImage';
import { useJsonData } from '../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

const iconMap: Record<string, LucideIcon> = {
  CheckCircle2,
  Eye,
  Target,
  Heart,
};

interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

interface FacilityItem {
  src: string;
  alt: string;
}

interface AboutData {
  values: ValueItem[];
  states: string[];
  facilities: FacilityItem[];
  stats: {
    founded: string;
    headquarters: string;
    wasteDiverted: string;
    statesServed: string;
  };
}

// Optimized animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export function About() {
  const { data, loading } = useJsonData<AboutData>('/data/about.json');
  const valuesRef = useRef(null);
  const presenceRef = useRef(null);
  const facilitiesRef = useRef(null);

  const valuesInView = useInView(valuesRef, { once: true, margin: '-100px' });
  const presenceInView = useInView(presenceRef, { once: true, margin: '-100px' });
  const facilitiesInView = useInView(facilitiesRef, { once: true, margin: '-100px' });

  // SSR-safe animation props
  const heroAnim = useSafeAnimation({ opacity: 0, y: 20 }, { opacity: 1, y: 0 });
  const storyLeftAnim = useSafeAnimation({ opacity: 0, x: -30 }, { opacity: 1, x: 0 });
  const storyRightAnim = useSafeAnimation({ opacity: 0, x: 30 }, { opacity: 1, x: 0 });
  const valuesHeaderAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    valuesInView
  );
  const valuesVariants = useSafeVariants(valuesInView);
  const presenceHeaderAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    presenceInView
  );
  const presenceVariants = useSafeVariants(presenceInView);
  const facilitiesHeaderAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 },
    facilitiesInView
  );
  const facilitiesVariants = useSafeVariants(facilitiesInView);

  const values = data?.values || [];
  const states = data?.states || [];
  const facilities = data?.facilities || [];
  const stats = data?.stats;

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>About Us | Eco Enviro Circular Solutions - Waste Management Company India</title>
        <meta
          name="title"
          content="About Us | Eco Enviro Circular Solutions - Waste Management Company India"
        />
        <meta
          name="description"
          content="Learn about Eco Enviro Circular Solutions - India's trusted waste management company headquartered in Ranchi, Jharkhand. Founded in 2020. 100,000+ MT waste diverted across 7 states."
        />
        <meta
          name="keywords"
          content="Eco Enviro about, waste management company India, Ranchi waste management, Jharkhand recycling, circular economy company, Jharkhand waste solutions"
        />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/about`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/about`} />
        <meta property="og:title" content="About Us | Eco Enviro Circular Solutions" />
        <meta
          property="og:description"
          content="India's trusted waste management company. Founded in 2020, headquartered in Ranchi. 100,000+ MT waste diverted across 7 states."
        />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content="About Us | Eco Enviro Circular Solutions" />
        <meta
          name="twitter:description"
          content="India's trusted waste management company. Founded in 2020, headquartered in Ranchi. 100,000+ MT waste diverted."
        />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-medium rounded-full mb-6">
              About Eco Enviro
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-eco-green-900 mb-6">
              Who We Are
            </h1>
            <p className="text-xl text-muted-foreground">
              We don't manage waste. We convert waste into resources. We build the circular economy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={storyLeftAnim.initial}
              animate={storyLeftAnim.animate}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Started in <strong className="text-eco-green-700">2020</strong> at Hazaribagh,
                  Jharkhand, Eco Enviro Circular Solutions is now headquartered in Ranchi,
                  Jharkhand.
                </p>
                <p>
                  We design and operate circular systems for plastic and paper, e-waste, ferrous and
                  non-ferrous scrap, and other recyclables and low-value waste streams.
                </p>
                <p>
                  With <strong className="text-eco-green-700">100,000+ MT</strong> of waste diverted
                  from landfills and an estimated{' '}
                  <strong className="text-eco-green-700">200,000-300,000 tCO₂e</strong> emissions
                  avoided, we're making a real impact on India's circular economy.
                </p>
                <p className="text-lg font-medium text-eco-green-800">Our focus is clear:</p>
                <ul className="space-y-2">
                  {[
                    'Maximum value recovery',
                    'Strong environmental and social compliance',
                    'Verified, responsible end-use',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-eco-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <motion.div
              initial={storyRightAnim.initial}
              animate={storyRightAnim.animate}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-eco-green-100 rounded-2xl p-6 text-center">
                <Calendar className="w-10 h-10 text-eco-green-600 mx-auto mb-3" />
                <p className="text-3xl font-bold text-eco-green-800">{stats?.founded || '2020'}</p>
                <p className="text-sm text-muted-foreground">Founded</p>
              </div>
              <div className="bg-eco-green-100 rounded-2xl p-6 text-center">
                <Building2 className="w-10 h-10 text-eco-green-600 mx-auto mb-3" />
                <p className="text-2xl font-bold text-eco-green-800">
                  {stats?.headquarters || 'Ranchi'}
                </p>
                <p className="text-sm text-muted-foreground">Headquarters</p>
              </div>
              <div className="bg-eco-green-100 rounded-2xl p-6 text-center">
                <Recycle className="w-10 h-10 mx-auto mb-3 text-eco-green-600" />
                <p className="text-2xl font-bold text-eco-green-800">
                  {stats?.wasteDiverted || '100K+'}
                </p>
                <p className="text-sm text-muted-foreground">MT Diverted</p>
              </div>
              <div className="bg-eco-green-100 rounded-2xl p-6 text-center">
                <Map className="w-10 h-10 mx-auto mb-3 text-eco-green-600" />
                <p className="text-2xl font-bold text-eco-green-800">
                  {stats?.statesServed || '7'}
                </p>
                <p className="text-sm text-muted-foreground">States Served</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-br from-eco-green-800 to-eco-green-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={heroAnim.initial}
              animate={heroAnim.animate}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 bg-eco-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-eco-green-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Vision</h3>
              <p className="text-eco-green-100 text-lg">
                Zero waste to landfill through scalable circular solutions.
              </p>
            </motion.div>

            <motion.div
              initial={heroAnim.initial}
              animate={heroAnim.animate}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <div className="w-14 h-14 bg-eco-green-500/20 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-eco-green-300" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Mission</h3>
              <ul className="space-y-3 text-eco-green-100">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-eco-green-400 flex-shrink-0 mt-0.5" />
                  <span>Recover maximum value from waste</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-eco-green-400 flex-shrink-0 mt-0.5" />
                  <span>Enable responsible disposal</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-eco-green-400 flex-shrink-0 mt-0.5" />
                  <span>Support India's circular economy goals and policies</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-eco-green-400 flex-shrink-0 mt-0.5" />
                  <span>Build compliance-ready waste systems</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={valuesHeaderAnim.initial}
            animate={valuesHeaderAnim.animate}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-4">Our Values</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-eco-green-50 rounded-2xl p-6 animate-pulse">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl mx-auto mb-4" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial={valuesVariants.initial}
              animate={valuesVariants.animate}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {values.map((value) => {
                const IconComponent = iconMap[value.icon] || CheckCircle2;
                return (
                  <motion.div
                    key={value.title}
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    className="bg-eco-green-50 rounded-2xl p-6 text-center hover:bg-eco-green-100 transition-colors"
                  >
                    <div className="w-14 h-14 bg-eco-green-200 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-eco-green-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-eco-green-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Our Presence */}
      <section ref={presenceRef} className="py-20 bg-eco-green-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={presenceHeaderAnim.initial}
            animate={presenceHeaderAnim.animate}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-4">Our Presence</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Eco Enviro operates across key states in India
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial={presenceVariants.initial}
            animate={presenceVariants.animate}
            className="flex flex-wrap justify-center gap-4"
          >
            {states.map((state) => (
              <motion.div
                key={state}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm border border-eco-green-100"
              >
                <MapPin className="w-4 h-4 text-eco-green-600" />
                <span className="font-medium text-eco-green-800">{state}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Facilities */}
      <section ref={facilitiesRef} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={facilitiesHeaderAnim.initial}
            animate={facilitiesHeaderAnim.animate}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-4">
              Our Facilities
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              State-of-the-art waste processing and recycling infrastructure
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial={facilitiesVariants.initial}
            animate={facilitiesVariants.animate}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {facilities.map((image) => (
              <motion.div
                key={image.src}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                className="relative overflow-hidden rounded-2xl group"
              >
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-eco-green-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <span className="text-white text-sm font-medium">{image.alt}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
